import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, Link2, Lock, LockOpen, Pencil, Plus, RefreshCw, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import {
  cambiarEstadoImportacion,
  desvincularCompra,
  getImportacion,
  type CompraImportacion,
  type ImportacionDetalle,
} from '@/shared/api/controllers/importaciones';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { listEntityFiles } from '@/shared/api/documents';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { FileUploader } from '@/shared/components/file-upload/FileUploader';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/money';
import { useRealtimeInvalidate } from '@/shared/realtime/useRealtimeInvalidate';
import { OrigenUnidadesSection } from '@/features/inventario/components/StockPorOrigen';
import { ArticulosImportacion, useArticulosImportacion } from './ArticulosImportacion';
import { BitacoraImportacion, useNotasImportacion } from './BitacoraImportacion';
import { EtapasImportacion } from './EtapasImportacion';
import { DesgloseCosto } from './DesgloseCosto';
import { ImportacionDialog } from './ImportacionDialog';
import { VincularComprasDialog } from './VincularComprasDialog';
import { EstadoImportacion, fechaImp, montosPorMoneda } from './componentes';

type Codigo = 'ARS' | 'USD' | 'MXN';

/** Pestañas del detalle, en orden. Las etapas van al final. */
const SECCIONES = [
  ['compras', 'Compras y pagos'],
  ['articulos', 'Artículos'],
  ['bitacora', 'Bitácora'],
  ['mercaderia', 'Mercadería recibida'],
  ['adjuntos', 'Adjuntos'],
  ['etapas', 'Etapas'],
] as const;
type Seccion = (typeof SECCIONES)[number][0];

/**
 * Detalle de una importación: compras asociadas, pagos, saldo y facturas. Las
 * compras se cargan en Compras y los pagos en Administración; acá sólo se ven.
 */
export function ImportacionDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const readOnly = useIsReadOnly();
  const { config: activeCountry } = useActiveCountry();
  const [editando, setEditando] = useState(false);
  const [vinculando, setVinculando] = useState(false);
  const [desvinculando, setDesvinculando] = useState<CompraImportacion | null>(null);
  const [params, setParams] = useSearchParams();
  const seccionUrl = params.get('seccion');
  // Sin la tabla de notas (migración sin aplicar) la pestaña Bitácora no aparece.
  const { q: notasQ } = useNotasImportacion(Number(id));
  const conBitacora = notasQ.data?.habilitado === true;
  const articulosQ = useArticulosImportacion(Number(id));
  const secciones = SECCIONES.filter(([k]) => k !== 'bitacora' || conBitacora);
  const seccion: Seccion = secciones.some(([k]) => k === seccionUrl) ? (seccionUrl as Seccion) : 'compras';
  const elegir = (s: Seccion) =>
    setParams(
      (prev) => {
        const q = new URLSearchParams(prev);
        q.set('seccion', s);
        return q;
      },
      { replace: true },
    );

  const q = useQuery({
    queryKey: ['importaciones', activeCountry.code, 'detalle', id],
    queryFn: () => getImportacion(Number(id)),
    // Al volver de cargar una compra tiene que aparecer, sin esperar el cache.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Los adjuntos viven en la carpeta del drive `Importaciones/{id}/`.
  const adjuntosKey = ['importaciones', activeCountry.code, 'adjuntos', id];
  const adjuntosQ = useQuery({
    queryKey: adjuntosKey,
    queryFn: () => listEntityFiles('Importacion', id!),
    enabled: !!id,
    staleTime: 15_000,
  });
  const refrescarAdjuntos = () => qc.invalidateQueries({ queryKey: adjuntosKey });

  useRealtimeInvalidate('purchase_headers', ['importaciones']);

  const estado = useMutation({
    mutationFn: (accion: 'cerrar' | 'reabrir') => cambiarEstadoImportacion(Number(id), accion),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['importaciones'] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo cambiar el estado.'),
  });

  const desvincular = useMutation({
    mutationFn: (compraId: string) => desvincularCompra(Number(id), compraId),
    onSuccess: () => {
      setDesvinculando(null);
      toast.success('La compra ya no está asociada a la importación.');
      qc.invalidateQueries({ queryKey: ['importaciones'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo sacar la compra.'),
  });

  // Sólo las monedas que deben algo ("US$ 815,37 + $ 0,00" confunde); todo pago, 0 en la moneda de la importación.
  const deudas = q.data
    ? Object.fromEntries(Object.entries(q.data.pendiente).filter(([, m]) => m > 0.01))
    : {};
  const saldoPendiente =
    !q.data || q.data.compras.length === 0
      ? '—'
      : Object.keys(deudas).length > 0
        ? montosPorMoneda(deudas)
        : formatMoney(0, q.data.moneda as Codigo);

  const volver = (
    <Button
      size="icon"
      className="h-10 w-10 shrink-0 rounded-full bg-foreground text-background hover:bg-foreground/90"
      onClick={() => navigate('/importaciones')}
      aria-label="Volver a Importaciones"
      title="Volver a Importaciones"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
    </Button>
  );

  if (q.isPending) return <Skeleton className="h-64 w-full rounded-2xl" />;
  if (q.isError || !q.data) {
    return (
      <div className="space-y-4">
        {volver}
        <p className="text-sm text-destructive">No se pudo cargar la importación.</p>
        <Button variant="outline" size="sm" onClick={() => q.refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }
  const imp: ImportacionDetalle = q.data;
  const abierta = imp.status === 'Abierta';
  const puedeAdjuntar = !readOnly && imp.status !== 'Anulada';

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto">
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        {volver}
        <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Importación N° {imp.numero}
        </h1>
        <EstadoImportacion status={imp.status} />
        <div className="ml-auto flex flex-wrap gap-2">
          {!readOnly && abierta && (
              <>
                <Button variant="outline" className="gap-2" onClick={() => setEditando(true)}>
                  <Pencil className="h-4 w-4" aria-hidden /> Editar
                </Button>
                <Button className="gap-2" disabled={estado.isPending} onClick={() => estado.mutate('cerrar')}>
                  <Lock className="h-4 w-4" aria-hidden /> Cerrar
                </Button>
              </>
          )}
          {!readOnly && imp.status === 'Cerrada' && (
            <Button
              variant="outline"
              className="gap-2"
              disabled={estado.isPending}
              onClick={() => estado.mutate('reabrir')}
            >
              <LockOpen className="h-4 w-4" aria-hidden /> Reabrir
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            onClick={() => qc.invalidateQueries({ queryKey: ['importaciones'] })}
            aria-label="Actualizar"
            title="Actualizar"
          >
            <RefreshCw className={cn('h-4 w-4', q.isFetching && 'animate-spin')} aria-hidden />
          </Button>
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-2 gap-3 md:grid-cols-4">
        <Dato titulo="Descripción" valor={imp.descripcion} />
        <Dato titulo="Fabricante" valor={imp.fabricante ?? '—'} />
        <Dato titulo="Fecha" valor={fechaImp(imp.fecha)} />
        <Dato
          titulo={imp.etapaArribo ? `Fecha probable (${imp.etapaArribo})` : 'Fecha probable'}
          valor={fechaImp(imp.fechaProbable)}
          detalle={<EstimadoOriginal imp={imp} />}
        />
      </div>

      {/* Secciones en pestañas (estilo segmentado, como SAM). La elegida queda en
          ?seccion= y todos los paneles quedan montados: cambiar de pestaña no
          pierde lo que se estaba cargando. */}
      <div
        role="tablist"
        aria-label="Secciones de la importación"
        className="flex w-fit max-w-full shrink-0 gap-0.5 overflow-x-auto rounded-lg border bg-card p-0.5"
      >
        {secciones.map(([k, titulo]) => (
          <button
            key={k}
            type="button"
            id={`pestana-${k}`}
            role="tab"
            aria-selected={seccion === k}
            aria-controls={`panel-${k}`}
            onClick={() => elegir(k)}
            className={cn(
              'whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold transition-all',
              seccion === k ? 'bg-muted text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {titulo}
            {k === 'compras' && imp.compras.length > 0 && ` (${imp.compras.length})`}
            {k === 'articulos' && articulosQ.data && articulosQ.data.length > 0 && ` (${articulosQ.data.length})`}
            {k === 'bitacora' && notasQ.data && notasQ.data.items.length > 0 && ` (${notasQ.data.items.length})`}
            {k === 'adjuntos' && adjuntosQ.data && adjuntosQ.data.length > 0 && ` (${adjuntosQ.data.length})`}
          </button>
        ))}
      </div>

      <div role="tabpanel" id="panel-compras" aria-labelledby="pestana-compras" hidden={seccion !== 'compras'}>
        {/* Compras y pagos lado a lado: los pagos son de esas compras. */}
        <div className="grid gap-4 xl:grid-cols-[3fr_2fr]">
          <section className="min-w-0 space-y-2">
            {/* Mismo alto que el título de Pagos, así las dos columnas arrancan parejas. */}
            <div className="flex min-h-9 flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold">Compras asociadas ({imp.compras.length})</h2>
              {!readOnly && abierta && (
                <div className="ml-auto flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setVinculando(true)}>
                    <Link2 className="h-4 w-4" aria-hidden /> Vincular existente
                  </Button>
                  <Button
                    size="sm"
                    className="gap-1.5 bg-foreground text-background hover:bg-foreground/90"
                    onClick={() => navigate(`/compras/nueva?importacion=${imp.id}`)}
                  >
                    Agregar compra <Plus className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              )}
            </div>
            {imp.compras.length === 0 ? (
              <EmptyNote>Todavía no hay compras asociadas.</EmptyNote>
            ) : (
              <div className="overflow-x-auto rounded-2xl border bg-card">
                <table className="w-full text-sm">
                  {/* Proveedor, comprobante, fecha y factura en una sola celda: con pagos
                      al lado la tabla es angosta y así las acciones siempre se ven. */}
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 font-medium">Compra</th>
                      <th className="px-4 py-2 text-right font-medium">Total</th>
                      <th className="px-4 py-2 text-right font-medium">Pendiente</th>
                      <th className="px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {imp.compras.map((c) => {
                      const moneda = c.moneda as Codigo;
                      return (
                        <tr key={c.id} className="align-top">
                          <td className="min-w-0 px-4 py-2">
                            <p className="font-medium text-foreground">{c.proveedor || '—'}</p>
                            <p className="text-xs text-muted-foreground">
                              {[c.comprobante, c.numero].filter(Boolean).join(' ') || '—'} · {fechaImp(c.fecha)}
                            </p>
                            {c.facturaDriveItemId && (
                              <div className="mt-1 max-w-[12rem]">
                                <FileLink
                                  entityType="Compra"
                                  entityId={c.id}
                                  fileName={c.facturaFileName ?? 'Factura'}
                                  driveItemId={c.facturaDriveItemId}
                                  allowDelete={false}
                                  compact
                                />
                              </div>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">{formatMoney(c.total, moneda)}</td>
                          {/* El estado va con lo pendiente: los dos hablan de cuánto se pagó. */}
                          <td className="whitespace-nowrap px-4 py-2 text-right">
                            <p className={cn('tabular-nums', c.restante > 0.01 && 'font-semibold text-amber-700')}>
                              {formatMoney(c.restante, moneda)}
                            </p>
                            <div className="mt-1">
                              <EstadoCompra status={c.status} />
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-2 py-1 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Ver compra"
                              aria-label="Ver compra"
                              onClick={() => navigate(`/compras/${c.id}`)}
                            >
                              <Eye className="h-4 w-4" aria-hidden />
                            </Button>
                            {!readOnly && abierta && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Sacar de la importación"
                                aria-label="Sacar de la importación"
                                onClick={() => setDesvinculando(c)}
                              >
                                <Unlink className="h-4 w-4" aria-hidden />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <section className="min-w-0 space-y-2">
            <div className="flex min-h-9 items-center">
              <h2 className="text-sm font-semibold">Pagos ({imp.pagos.length})</h2>
            </div>
            {imp.pagos.length === 0 ? (
              <EmptyNote>Sin pagos. Se registran desde Administración.</EmptyNote>
            ) : (
              <div className="overflow-x-auto rounded-2xl border bg-card">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 font-medium">Pago</th>
                      <th className="px-4 py-2 text-right font-medium">Importe</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {imp.pagos.map((p) => {
                      // El comprobante de la compra dice más que su id interno.
                      const compra = imp.compras.find((c) => c.id === p.compraId);
                      const comprobante = compra
                        ? [compra.comprobante, compra.numero].filter(Boolean).join(' ')
                        : `Compra ${p.compraId}`;
                      return (
                        <tr key={p.id} className="align-top">
                          <td className="min-w-0 px-4 py-2">
                            <p className="font-medium text-foreground">{p.proveedor || '—'}</p>
                            <p className="text-xs text-muted-foreground">
                              {p.fecha ?? '—'} · {comprobante || '—'}
                            </p>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                            {formatMoney(p.importe, p.moneda as Codigo)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
        {imp.compras.length > 0 && (
          <div className="mt-4">
            <DesgloseCosto desglose={imp.desglose} />
          </div>
        )}
      </div>

      {conBitacora && (
        <div role="tabpanel" id="panel-bitacora" aria-labelledby="pestana-bitacora" hidden={seccion !== 'bitacora'}>
          <BitacoraImportacion importacionId={imp.id} />
        </div>
      )}

      <div role="tabpanel" id="panel-articulos" aria-labelledby="pestana-articulos" hidden={seccion !== 'articulos'}>
        <ArticulosImportacion importacionId={imp.id} />
      </div>

      <div role="tabpanel" id="panel-mercaderia" aria-labelledby="pestana-mercaderia" hidden={seccion !== 'mercaderia'}>
        <OrigenUnidadesSection kind="importacion" id={String(imp.id)} />
      </div>

      <div role="tabpanel" id="panel-adjuntos" aria-labelledby="pestana-adjuntos" hidden={seccion !== 'adjuntos'}>
        <section className="space-y-2">
          <div className="space-y-2 rounded-2xl border bg-card p-4">
            {adjuntosQ.isPending ? (
              <p className="text-xs text-muted-foreground">Cargando archivos…</p>
            ) : adjuntosQ.isError ? (
              <p className="text-xs text-destructive">No se pudieron cargar los adjuntos.</p>
            ) : adjuntosQ.data.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {adjuntosQ.data.map((f) => (
                  <FileLink
                    key={f.driveItemId}
                    entityType="Importacion"
                    entityId={String(imp.id)}
                    driveItemId={f.driveItemId}
                    fileName={f.fileName}
                    allowDelete={puedeAdjuntar}
                    onDeleted={refrescarAdjuntos}
                    compact
                  />
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-dashed bg-card/40 px-3 py-2 text-xs text-muted-foreground">Sin adjuntos.</p>
            )}
            {puedeAdjuntar && (
              <>
                <p className="text-xs text-muted-foreground">
                  Invoice, proforma u otros documentos del contenedor (PDF o imagen, hasta 4 MB).
                </p>
                <FileUploader
                  entityType="Importacion"
                  entityId={String(imp.id)}
                  compact
                  emptyHint={
                    adjuntosQ.data && adjuntosQ.data.length > 0 ? 'Agregar otro archivo' : 'Subir archivo'
                  }
                  onUploaded={refrescarAdjuntos}
                />
              </>
            )}
          </div>
        </section>
      </div>

      <div role="tabpanel" id="panel-etapas" aria-labelledby="pestana-etapas" hidden={seccion !== 'etapas'}>
        <EtapasImportacion imp={imp} puedeEditar={!readOnly && abierta} />
      </div>

      {/* Totales abajo y fijos, como en el resto de la app (Compras, Administración).
          En el teléfono, de a dos por fila y con el monto debajo, para no tapar media pantalla. */}
      <div className="sticky bottom-0 mt-auto grid shrink-0 grid-cols-2 gap-1.5 rounded-2xl border bg-card px-3 py-2.5 text-xs shadow-sm sm:flex sm:flex-wrap sm:items-center sm:gap-2 sm:px-4 sm:py-3">
        <TotalPill label="Total FOB">
          {imp.totalFob != null ? formatMoney(imp.totalFob, imp.moneda) : 'Sin definir'}
        </TotalPill>
        {imp.fobPorInstrumentar != null ? (
          <TotalPill
            label={imp.fobPorInstrumentar < 0 ? 'FOB instrumentado de más' : 'FOB por instrumentar'}
            emphasize={imp.fobPorInstrumentar < 0}
          >
            {formatMoney(Math.abs(imp.fobPorInstrumentar), imp.moneda)}
          </TotalPill>
        ) : (
          <TotalPill label="FOB instrumentado">{formatMoney(imp.fobInstrumentado, imp.moneda)}</TotalPill>
        )}
        {/* Las monedas no se convierten: una factura del fabricante en otra moneda queda
            fuera del FOB y se avisa, para que "por instrumentar" no se lea de más. */}
        {imp.fobOtraMoneda.compras > 0 && (
          <span
            className="col-span-2 sm:col-span-1"
            title={`${imp.fobOtraMoneda.compras === 1 ? 'Una compra' : `${imp.fobOtraMoneda.compras} compras`} del fabricante en otra moneda que la importación (${imp.moneda}). No se convierten, así que no suman al FOB instrumentado.`}
          >
            <TotalPill label={`Fuera del FOB (${imp.fobOtraMoneda.compras === 1 ? '1 compra' : `${imp.fobOtraMoneda.compras} compras`} en otra moneda)`} emphasize>
              {montosPorMoneda(imp.fobOtraMoneda.montos)}
            </TotalPill>
          </span>
        )}
        <TotalPill label="Total compras">{montosPorMoneda(imp.total)}</TotalPill>
        <TotalPill label="Saldo pendiente" emphasize={Object.values(imp.pendiente).some((m) => m > 0.01)}>
          {saldoPendiente}
        </TotalPill>
      </div>

      <ImportacionDialog open={editando} importacion={imp} onClose={() => setEditando(false)} />

      {vinculando && (
        <VincularComprasDialog
          importacionId={imp.id}
          numero={imp.numero}
          onClose={() => setVinculando(false)}
          onDone={() => {
            setVinculando(false);
            qc.invalidateQueries({ queryKey: ['importaciones'] });
          }}
        />
      )}

      <Dialog open={desvinculando != null} onOpenChange={(o) => !o && !desvincular.isPending && setDesvinculando(null)}>
        <DialogContent className="max-w-md" processing={desvincular.isPending && 'Sacando…'}>
          <DialogHeader>
            <DialogTitle>Sacar compra de la importación</DialogTitle>
            <DialogDescription>
              {desvinculando &&
                `La compra de ${desvinculando.proveedor || '—'} ${[desvinculando.comprobante, desvinculando.numero].filter(Boolean).join(' ')} deja de estar asociada a la importación N° ${imp.numero}. La compra, sus pagos y su stock no cambian.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDesvinculando(null)} disabled={desvincular.isPending}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={desvincular.isPending}
              onClick={() => desvinculando && desvincular.mutate(desvinculando.id)}
            >
              Sacar de la importación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Estado de la compra con el mismo color que en Compras: pendiente en ámbar, pagada en verde. */
function EstadoCompra({ status }: { status: string }) {
  const clases =
    status === 'Pagado'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'Pendiente' || status === 'Pago Parcial'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-slate-100 text-slate-600';
  return (
    <span className={cn('inline-flex whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide', clases)}>
      {status || '—'}
    </span>
  );
}

function Dato({
  titulo,
  valor,
  alerta,
  detalle,
}: {
  titulo: string;
  valor: string;
  alerta?: boolean;
  detalle?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{titulo}</p>
      <p className={alerta ? 'mt-0.5 text-sm font-semibold text-destructive' : 'mt-0.5 text-sm font-semibold text-foreground'}>
        {valor}
      </p>
      {detalle}
    </div>
  );
}

const DIA_MS = 86_400_000;

/**
 * El estimado del alta contra el de hoy. La diferencia se mide en días de
 * duración (fecha → fecha probable), así corregir la fecha no se lee como atraso.
 */
function EstimadoOriginal({ imp }: { imp: ImportacionDetalle }) {
  if (imp.diasEstimadosOriginal == null) return null;
  const actual = Math.round((Date.parse(imp.fechaProbable) - Date.parse(imp.fecha)) / DIA_MS);
  const diferencia = actual - imp.diasEstimadosOriginal;
  return (
    <p
      className="mt-0.5 text-xs text-muted-foreground"
      title={imp.fechaProbableOriginal ? `Fecha probable al crearla: ${fechaImp(imp.fechaProbableOriginal)}` : undefined}
    >
      Estimado inicial: {imp.diasEstimadosOriginal} días
      {diferencia !== 0 && (
        <span className={cn('font-semibold', diferencia > 0 ? 'text-amber-700' : 'text-emerald-700')}>
          {' · '}
          {diferencia > 0 ? `+${diferencia}` : diferencia} días
        </span>
      )}
    </p>
  );
}

function TotalPill({
  label,
  children,
  emphasize,
}: {
  label: string;
  children: React.ReactNode;
  emphasize?: boolean;
}) {
  return (
    <span
      className={cn(
        'flex min-w-0 flex-col justify-center rounded-lg border px-2.5 py-1.5 sm:inline-flex sm:h-9 sm:flex-row sm:items-center sm:gap-2 sm:px-3 sm:py-0',
        emphasize ? 'border-amber-300 bg-amber-50' : 'bg-secondary',
      )}
    >
      <span className="truncate text-[10px] text-muted-foreground sm:text-xs">{label}</span>
      <span className={cn('truncate font-semibold tabular-nums', emphasize ? 'text-amber-800' : 'text-foreground')}>
        {children}
      </span>
    </span>
  );
}

/** Aviso de sección vacía, como en el resto de la app (recuadro punteado). */
function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg border border-dashed bg-card/40 px-4 py-6 text-center text-sm text-muted-foreground">
      {children}
    </p>
  );
}
